import { DropdownSelect } from '../ui/DropdownSelect';
import { Field } from '../ui/Field';
import {
  usePaymentTypes,
  useProjectTypes,
  useTaskTypes,
} from '../../hooks/useTemplateTypes';

/**
 * Phase 41 Plan 41-10 (Surface 1) — three-template trio selector for the
 * project create / edit form.
 *
 * - Renders 3 native `<DropdownSelect>` controls (Payment template / Project
 *   type / Task template) wrapped by `<Field>` for label affordances.
 * - When a value is null/undefined, the control falls back to the baseline
 *   template id (`1`) — Phase 35 convention is that template id `1` is the
 *   "Default" baseline across all three sets.
 * - The `disabled` + `disabledReason` props grey the dropdowns out and
 *   explain why. MKS-251 retired the TPL-04 locked-after-activity guard they
 *   used to carry; the remaining reason is authorization (the caller is not
 *   admin / finance / the project's PM).
 *
 * Per CLAUDE.md D-Copy gate: zero Czech strings. Labels are English-only.
 */

export interface TemplateTrioValue {
  paymentTypeId: number | null;
  projectTypeId: number | null;
  taskTypeId: number | null;
}

export type TemplateTrioAnchor = 'payment' | 'projectType' | 'task';

export interface ProjectFormTemplateSelectorProps {
  value: TemplateTrioValue;
  onChange: (next: TemplateTrioValue, anchor: TemplateTrioAnchor) => void;
  disabled?: boolean;
  disabledReason?: string;
  /**
   * UAT 2026-05-13 — caller may render the trio in two passes (e.g. Payment
   * first, then a conditional settings block, then Project type + Task
   * template). `only` filters which dropdowns this instance renders. Defaults
   * to all three (current behavior).
   */
  only?: Array<TemplateTrioAnchor>;
  /** Show the amber locked banner. Suppress on secondary instances. */
  showLockBanner?: boolean;
  /**
   * MKS-211 — when present, narrows the Payment dropdown to this id list for
   * the create-mode gate: the Default template is retired, so a fresh
   * project can only pick Time & Material or Fixed Price here (MKS-251 adds
   * the SLA Hour Package retainer into this form directly). Absent = today's
   * behaviour, unchanged (D-08) — every active payment type renders, Default
   * included.
   * Only narrows the Payment column; Project type / Task template are
   * unaffected.
   */
  allowedPaymentTypeIds?: number[];
}

export const BASELINE_TEMPLATE_ID = 1;

const COLUMN_CLASS: Record<number, string> = {
  1: 'md:grid-cols-1',
  2: 'md:grid-cols-2',
  3: 'md:grid-cols-3',
};

export function ProjectFormTemplateSelector({
  value,
  onChange,
  disabled,
  disabledReason,
  only,
  showLockBanner = true,
  allowedPaymentTypeIds,
}: ProjectFormTemplateSelectorProps) {
  const pay = usePaymentTypes();
  const proj = useProjectTypes();
  const tsk = useTaskTypes();

  const pickValue = (id: number | null, fallback: number) => String(id ?? fallback);

  const visible = only ?? ['payment', 'projectType', 'task'];
  const colClass = COLUMN_CLASS[visible.length] ?? 'md:grid-cols-3';

  // MKS-211 — narrow the Payment options to `allowedPaymentTypeIds` when
  // present. Two corrections vs. a naive filter:
  //   1. Always keep the currently-selected id in the rendered option set
  //      even when it falls outside the allowed list — a retainer (SLA Hour
  //      Package) set elsewhere must not leave the native <select> holding a
  //      value no <option> carries.
  //   2. When the prop is present and value.paymentTypeId is null, the
  //      fallback is the first allowed id, not BASELINE_TEMPLATE_ID — the
  //      baseline (Default) id is the very thing being hidden.
  const paymentOptions = (pay.data?.data ?? []).filter((t) => t.isActive);
  const visiblePaymentOptions = allowedPaymentTypeIds
    ? paymentOptions.filter(
        (t) => allowedPaymentTypeIds.includes(t.id) || t.id === value.paymentTypeId,
      )
    : paymentOptions;
  const paymentValue = allowedPaymentTypeIds
    ? String(value.paymentTypeId ?? allowedPaymentTypeIds[0] ?? BASELINE_TEMPLATE_ID)
    : pickValue(value.paymentTypeId, BASELINE_TEMPLATE_ID);

  return (
    <div className="space-y-3">
      {showLockBanner && disabled && disabledReason && (
        <div
          className="px-3 py-2 rounded-md bg-amber-50 border border-amber-200 text-[12px] text-amber-900"
          role="status"
        >
          {/* Lowercase ascii label keeps the lock surface free of glyph dependencies. */}
          <span className="font-semibold">Locked:</span> {disabledReason}
        </div>
      )}
      <div className={`grid grid-cols-1 ${colClass} gap-3`}>
        {visible.includes('payment') && (
          <Field label="Payment template">
            <DropdownSelect
              disabled={disabled}
              value={paymentValue}
              onChange={(e) =>
                onChange(
                  { ...value, paymentTypeId: Number(e.target.value) },
                  'payment',
                )
              }
            >
              {visiblePaymentOptions.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.name}
                </option>
              ))}
            </DropdownSelect>
          </Field>
        )}
        {visible.includes('projectType') && (
          <Field label="Project type">
            <DropdownSelect
              disabled={disabled}
              value={pickValue(value.projectTypeId, BASELINE_TEMPLATE_ID)}
              onChange={(e) =>
                onChange(
                  { ...value, projectTypeId: Number(e.target.value) },
                  'projectType',
                )
              }
            >
              {(proj.data?.data ?? [])
                .filter((t) => t.isActive)
                .map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.name}
                  </option>
                ))}
            </DropdownSelect>
          </Field>
        )}
        {visible.includes('task') && (
          <Field label="Task template">
            <DropdownSelect
              disabled={disabled}
              value={pickValue(value.taskTypeId, BASELINE_TEMPLATE_ID)}
              onChange={(e) =>
                onChange(
                  { ...value, taskTypeId: Number(e.target.value) },
                  'task',
                )
              }
            >
              {(tsk.data?.data ?? [])
                .filter((t) => t.isActive)
                .map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.name}
                  </option>
                ))}
            </DropdownSelect>
          </Field>
        )}
      </div>
    </div>
  );
}
